/**
 * A Map with a fixed maximum size that evicts the oldest entries when full.
 * Implements FIFO (First In, First Out) eviction strategy.
 */
export class LimitedSizeMap<K, V> implements Iterable<[K, V]> {
    private _map: Map<K, V>;
    private readonly _maxSize: number;

    constructor(maxSize: number) {
        if (maxSize <= 0) {
            throw new Error("Maximum size must be greater than 0");
        }
        this._maxSize = maxSize;
        this._map = new Map();
    }

    set(key: K, value: V): this {
        if (!this._map.has(key) && this._map.size >= this._maxSize) {
            const firstKey = this._map.keys().next().value;
            this._map.delete(firstKey);
        }
        this._map.set(key, value);
        return this;
    }

    get(key: K): V | undefined {
        return this._map.get(key);
    }

    has(key: K): boolean {
        return this._map.has(key);
    }

    delete(key: K): boolean {
        return this._map.delete(key);
    }

    clear(): void {
        this._map.clear();
    }

    forEach(callback: (value: V, key: K, map: Map<K, V>) => void, thisArg?: any): void {
        this._map.forEach(callback, thisArg);
    }

    get size(): number {
        return this._map.size;
    }

    get maxSize(): number {
        return this._maxSize;
    }

    keys(): IterableIterator<K> {
        return this._map.keys();
    }

    values(): IterableIterator<V> {
        return this._map.values();
    }

    entries(): IterableIterator<[K, V]> {
        return this._map.entries();
    }

    [Symbol.iterator](): IterableIterator<[K, V]> {
        return this._map[Symbol.iterator]();
    }
}
